import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services/authService';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser]     = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount: restore session from localStorage token
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      authService.me()
        .then(response => setUser(response.data))
        .catch(() => {
          localStorage.removeItem('token');
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // Step 1 — credentials. Returns one of:
  //   { requires_2fa: true, partial_token }           → show TOTP input
  //   { requires_2fa_setup: true, setup_token, email_hint } → go to /setup-2fa
  //   (fully logged in)                               → user set in state
  const login = async (username, password) => {
    const response = await authService.login({ username, password });
    const data = response.data;

    if (data.requires_2fa) {
      return { requires_2fa: true, partial_token: data.partial_token };
    }

    if (data.requires_2fa_setup) {
      // Store restricted setup_token in sessionStorage — NOT a real auth token
      sessionStorage.setItem('setup_token', data.setup_token);
      sessionStorage.setItem('setup_email_hint', data.email_hint || '');
      return { requires_2fa_setup: true };
    }

    // Fully authenticated
    localStorage.setItem('token', data.token);
    setUser(data.user);
    return data;
  };

  // Step 2 (only when requires_2fa was true) — verify TOTP code
  const verify2fa = useCallback(async (partial_token, code) => {
    const response = await authService.verify2fa({ partial_token, code });
    const data = response.data;
    localStorage.setItem('token', data.token);
    setUser(data.user);
    return data;
  }, []);

  // Called after successful 2FA enrollment with the real token + user
  const complete2faSetup = useCallback((token, userData) => {
    localStorage.setItem('token', token);
    setUser(userData);
    sessionStorage.removeItem('setup_token');
    sessionStorage.removeItem('setup_email_hint');
  }, []);

  const register = async (userData) => authService.register(userData);

  // Forced/voluntary password change. Backend rotates the token and clears must_change_password;
  // we store the new token and refetch the user so the gate (must_change_password) clears.
  const changePassword = useCallback(async (current_password, new_password) => {
    const { data } = await authService.changePassword({ current_password, new_password });
    if (data.token) localStorage.setItem('token', data.token);
    const me = await authService.me();
    setUser(me.data);
    return me.data;
  }, []);

  const logout = async () => {
    try { await authService.logout(); } catch (_) {}
    localStorage.removeItem('token');
    sessionStorage.removeItem('setup_token');
    sessionStorage.removeItem('setup_email_hint');
    setUser(null);
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    verify2fa,
    complete2faSetup,
    changePassword,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
